// What the group's workflow version requires and how each requirement is met right now
// (GET /api/task-groups/{id}/requirements): a task starts only when none is missing or broken.
import { CheckCircle, ListChecks, WarningCircle } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";

import { shortId } from "../../pages/format";
import { CollapsibleCard } from "../../ui/CollapsibleCard";
import { EmptyState, ErrorState, Loading } from "../../ui/states";
import { Mono, Pill } from "../../ui/status";
import { Chip, Segments, SummaryRow } from "../../ui/summary";
import { requirementLabel } from "../../workflows/requirements/model";
import { groupRequirementsQuery } from "../api";
import { REQUIREMENT_TONE } from "../display";
import type { GroupRequirements } from "../model";
import { useT } from "../../i18n";
import { requirementsSummary } from "./summaries";

/** A Cursor API key (D67) is accepted in requirements but refused at run time until Cursor runs. */
const isCursor = (r: GroupRequirements["requirements"][number]["requirement"]) =>
  r.kind === "credential" && (r.credential_kind as string) === "cursor_api_key";

function Ready({ satisfied }: { satisfied: boolean }) {
  const t = useT().tasks.group;
  return satisfied ? (
    <Pill tone="green" icon={<CheckCircle weight="fill" />}>
      {t.canStart}
    </Pill>
  ) : (
    <Pill tone="red" icon={<WarningCircle weight="fill" />}>
      {t.blocked}
    </Pill>
  );
}

function List({ data }: { data: GroupRequirements }) {
  const { tasks } = useT();
  const t = tasks.group;
  if (data.requirements.length === 0) {
    return (
      <EmptyState compact icon={<ListChecks weight="duotone" />} title={t.noRequirements}>
        {t.noRequirementsBody}
      </EmptyState>
    );
  }
  return (
    <ul className="m-0 list-none divide-y divide-muted/70 p-0">
      {data.requirements.map((r) => (
        <li key={r.index} className="flex flex-wrap items-center justify-between gap-2 py-3 first:pt-0 last:pb-0">
          <div className="min-w-0">
            <div className="text-[13.5px] font-medium text-gray-900">
              {requirementLabel(r.requirement)}
              {isCursor(r.requirement) ? <span className="ms-2 text-xs font-normal text-gray-500">{t.cursorSoon}</span> : null}
            </div>
            {r.binding && r.binding !== "platform" ? (
              <span title={r.binding}>
                <Mono className="text-[11.5px] text-gray-500">{t.credential(shortId(r.binding))}</Mono>
              </span>
            ) : null}
          </div>
          <Pill tone={REQUIREMENT_TONE[r.state]}>{tasks.requirementState[r.state]}</Pill>
        </li>
      ))}
    </ul>
  );
}

function Summary({ data }: { data: GroupRequirements }) {
  const c = useT().tasks.cards.requirements;
  const s = requirementsSummary(data);
  if (s.total === 0) {
    return (
      <SummaryRow>
        <Chip>{c.none}</Chip>
      </SummaryRow>
    );
  }
  const Icon = data.satisfied ? CheckCircle : WarningCircle;
  return (
    <SummaryRow>
      <Chip tone={s.tone} icon={<Icon weight="fill" aria-hidden />} title={c.detail(s.ready, s.total)}>
        {c.ready(s.ready, s.total)}
      </Chip>
      <Segments tones={s.tones} label={c.detail(s.ready, s.total)} />
    </SummaryRow>
  );
}

export function RequirementsCard({ groupId }: { groupId: string }) {
  const t = useT().tasks.group;
  const query = useQuery(groupRequirementsQuery(groupId));
  return (
    <CollapsibleCard
      kind="requirements"
      icon={<ListChecks weight="duotone" />}
      title={t.requirements}
      description={t.requirementsDescription}
      action={query.data ? <Ready satisfied={query.data.satisfied} /> : undefined}
      summary={query.data ? <Summary data={query.data} /> : null}
    >
      {query.isPending ? <Loading /> : null}
      {query.isError ? <ErrorState message={query.error.message} /> : null}
      {query.data ? <List data={query.data} /> : null}
    </CollapsibleCard>
  );
}
